import { EngineIcon } from '@phosphor-icons/react';
import {
  AdvancedMarker,
  APIProvider,
  Map,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps';
import { useEffect } from 'react';

import { Chip } from '@/components/common/chip';
import { color } from '@/lib/design-tokens';
import { env } from '@/lib/env';

import { formatCropYear, formatUpTo2Decimals } from '../details-rules';
import type { DeviceLocation } from '../details-schemas';

import styles from './device-location-card.module.scss';

export type DeviceLocationCardProps = {
  location: DeviceLocation | null;
};

/**
 * Draws the field boundary. `@vis.gl/react-google-maps` exports no Polygon component, so the rings
 * go on through the Maps API and are torn down when they change.
 *
 * The library comes from `useMapsLibrary` rather than the `google` global: `tsconfig.app.json`
 * pins `types` to `vite/client`, so the ambient namespace is deliberately out of scope.
 *
 * **The outline is dashed**, as the design draws it, and that needs two overlays rather than one:
 * a Maps `Polygon` has no dash option at all, and its `strokeOpacity` is the only lever. So the
 * polygon paints the fill with its stroke turned off, and a closed `Polyline` — which *does* take
 * repeating `icons` — paints the dashes on top of the same ring.
 *
 * The Maps API takes plain colour strings, so values come off the token source rather than an
 * inlined hex. `surface.page` is the design's own near-white (#FCFCF7 against its sampled #FCFFF5),
 * which reads against satellite imagery without the harshness of pure white.
 */
function FieldBoundary({ rings }: { rings: { lat: number; lng: number }[][] }) {
  const map = useMap();
  const mapsLib = useMapsLibrary('maps');

  useEffect(() => {
    if (!map || !mapsLib) return;

    // `fields/schemas.ts` deliberately puts no minimum length on a Boundary ring — a malformed
    // Boundary is a *geometric* failure, not a schema one — so `boundary: [[]]` parses and arrives
    // here intact. A ring of fewer than three points has no outline to draw, and handing a
    // `Polyline` a path with an `undefined` in it throws `InvalidValueError` inside this effect and
    // takes the whole card down. Same `>= 3` filter the sibling path already applies in
    // `details-schemas.ts`.
    const drawable = rings.filter((ring) => ring.length >= 3);
    if (drawable.length === 0) return;

    const overlays = drawable.flatMap((ring) => [
      new mapsLib.Polygon({
        paths: ring,
        map,
        // The dashes are the Polyline's job; a solid polygon stroke underneath would fill the gaps.
        strokeOpacity: 0,
        fillColor: color.surface.page,
        fillOpacity: 0.16,
        clickable: false,
      }),
      new mapsLib.Polyline({
        // Closed explicitly: a Polyline does not join its last point back to its first.
        // `slice(0, 1)` rather than `ring[0]!`: the filter above already guarantees a first point,
        // and this states it without an assertion the type checker cannot verify.
        path: [...ring, ...ring.slice(0, 1)],
        map,
        // `icons` are only drawn where the line itself is invisible, hence zero opacity here.
        strokeOpacity: 0,
        icons: [
          {
            icon: {
              path: 'M 0,-1 0,1',
              strokeColor: color.surface.page,
              strokeOpacity: 0.95,
              strokeWeight: 2,
              scale: 3,
            },
            offset: '0',
            repeat: '12px',
          },
        ],
        clickable: false,
      }),
    ]);

    return () => overlays.forEach((overlay) => overlay.setMap(null));
  }, [map, mapsLib, rings]);

  return null;
}

/**
 * The "Device Location" card (US-112437 §5): a satellite view centred on the device, the field
 * boundary when it sits inside one, a pin at its exact location, the crop chip and crop year, and
 * the field name with "City, State | acres" beneath.
 *
 * Three states the AC calls out explicitly:
 *
 * - **No location yet** (the device has never transmitted) — an empty map state, no field row.
 * - **Located but outside any field** — just the pin, and no field name/acres row at all.
 * - **Located inside a field** — the full treatment.
 *
 * Other devices and wells in the field are deliberately not drawn.
 */
export function DeviceLocationCard({ location }: DeviceLocationCardProps) {
  const apiKey = env.VITE_GOOGLE_MAPS_API_KEY;
  // Narrowed once into a value the map can consume, rather than re-asserting `lat!`/`lng!` at
  // each of the three use sites.
  const center =
    location?.lat != null && location?.lng != null
      ? { lat: location.lat, lng: location.lng }
      : null;
  const acres = formatUpTo2Decimals(location?.acres ?? null);
  const cropYear = formatCropYear(location?.cropYear ?? null);

  const meta =
    location?.fieldName || location?.city || location?.state ? (
      <div className={styles.meta}>
        {location?.fieldName ? <p className={styles.fieldName}>{location.fieldName}</p> : null}
        <p className={styles.place}>
          {[location?.city, location?.state].filter(Boolean).join(', ')}
          {acres ? ` | ${acres} Acres` : ''}
        </p>
      </div>
    ) : null;

  return (
    <section className={styles.card} aria-label="Device location">
      <h2 className={styles.title}>Device Location</h2>

      <div className={styles.frame}>
        {!center ? (
          <div className={styles.empty}>
            <p className={styles.emptyText}>No location reported yet</p>
          </div>
        ) : !apiKey ? (
          // The key is optional in the env schema, so the card degrades instead of crashing.
          <div className={styles.empty}>
            <p className={styles.emptyText}>Map unavailable — no Google Maps key configured</p>
          </div>
        ) : (
          <div className={styles.mapWrap}>
            <APIProvider apiKey={apiKey}>
              <Map
                mapId="fieldpro-device-location"
                defaultCenter={center}
                defaultZoom={16}
                mapTypeId="satellite"
                disableDefaultUI
                // Fixed view: this card reports where the device is, it is not a map to explore.
                // `none` stops drag, scroll-zoom and pinch alike; `clickableIcons` stops a tap on
                // a Google POI opening its info window, and the keyboard shortcuts would otherwise
                // still pan it with the arrow keys. The Map surface is where panning belongs.
                gestureHandling="none"
                clickableIcons={false}
                keyboardShortcuts={false}
                disableDoubleClickZoom
                className={styles.map}
              >
                <FieldBoundary rings={location?.boundary ?? []} />
                <AdvancedMarker position={center}>
                  {/* The design puts the device glyph inside the marker rather than leaving it a
                      bare dot, so a device reads as equipment at a glance. */}
                  <span className={styles.pin} aria-label="Device location">
                    <EngineIcon size={14} weight="fill" aria-hidden className={styles.pinIcon} />
                  </span>
                </AdvancedMarker>
              </Map>
            </APIProvider>

            {location?.cropName ? (
              <Chip intent="neutral" className={styles.crop}>
                {location.cropName}
              </Chip>
            ) : null}
            {cropYear ? (
              <div className={styles.cropYear}>
                <span className={styles.cropYearLabel}>Crop Year</span>
                <span className={styles.cropYearValue}>{cropYear}</span>
              </div>
            ) : null}
          </div>
        )}

        {meta}
      </div>
    </section>
  );
}
